package org.zq.astrock_ai.ui.components

import org.zq.astrock_ai.theme.LocalAppFontFamily

import androidx.compose.animation.core.LinearEasing
import androidx.compose.animation.core.RepeatMode
import androidx.compose.animation.core.animateFloat
import androidx.compose.animation.core.infiniteRepeatable
import androidx.compose.animation.core.rememberInfiniteTransition
import androidx.compose.animation.core.tween
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.wrapContentWidth
import androidx.compose.runtime.mutableFloatStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.layout.onGloballyPositioned
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.unit.IntOffset
import kotlin.math.roundToInt
import androidx.compose.ui.draw.clipToBounds
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.offset
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.text.font.FontFamily
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import org.zq.astrock_ai.data.IndexQuote
import org.zq.astrock_ai.data.MockStockData
import org.zq.astrock_ai.theme.BinanceColors
import org.zq.astrock_ai.ui.AppScreen

@Composable
fun TopNavBar(
    compact: Boolean = false,
    currentScreen: AppScreen = AppScreen.StockDetail,
    onScreenChange: (AppScreen) -> Unit = {},
    isLive: Boolean = false,
) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .background(BinanceColors.Dark.copy(alpha = 0.95f))
            .border(1.dp, BinanceColors.Hairline)
            .padding(
                horizontal = if (compact) 12.dp else 24.dp,
                vertical = if (compact) 10.dp else 12.dp,
            ),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.SpaceBetween,
    ) {
        Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.weight(1f)) {
            Box(
                modifier = Modifier
                    .size(if (compact) 32.dp else 36.dp)
                    .clip(RoundedCornerShape(8.dp))
                    .background(BinanceColors.Yellow),
                contentAlignment = Alignment.Center,
            ) {
                Text("◆", color = BinanceColors.Dark, fontWeight = FontWeight.Bold, fontSize = if (compact) 14.sp else 16.sp)
            }
            Spacer(Modifier.width(if (compact) 8.dp else 12.dp))
            Text(
                "智启股势",
                color = BinanceColors.Yellow,
                fontWeight = FontWeight.Bold,
                fontSize = if (compact) 15.sp else 20.sp,
                maxLines = 1,
                softWrap = false,
            )
            if (!compact) {
                Spacer(Modifier.width(8.dp))
                Text(
                    "PRO",
                    color = BinanceColors.Muted,
                    fontSize = 13.sp,
                    modifier = Modifier
                        .clip(RoundedCornerShape(4.dp))
                        .background(BinanceColors.Card)
                        .border(1.dp, BinanceColors.Hairline, RoundedCornerShape(4.dp))
                        .padding(horizontal = 8.dp, vertical = 2.dp),
                )
                if (isLive) {
                    Spacer(Modifier.width(6.dp))
                    Text(
                        "实时",
                        color = BinanceColors.Green,
                        fontSize = 11.sp,
                        modifier = Modifier
                            .clip(RoundedCornerShape(4.dp))
                            .background(BinanceColors.Green.copy(alpha = 0.12f))
                            .padding(horizontal = 6.dp, vertical = 2.dp),
                    )
                }
            }
        }

        if (!compact) {
            Row(horizontalArrangement = Arrangement.spacedBy(4.dp)) {
                NavTab("智启研究", active = currentScreen == AppScreen.Research) {
                    onScreenChange(AppScreen.Research)
                }
            }
        } else {
            Text(
                "PRO",
                color = BinanceColors.Muted,
                fontSize = 11.sp,
                modifier = Modifier
                    .clip(RoundedCornerShape(4.dp))
                    .background(BinanceColors.Card)
                    .border(1.dp, BinanceColors.Hairline, RoundedCornerShape(4.dp))
                    .padding(horizontal = 8.dp, vertical = 2.dp),
            )
        }
    }
}

@Composable
private fun NavTab(label: String, active: Boolean, onClick: () -> Unit) {
    Text(
        text = label,
        fontSize = 14.sp,
        fontWeight = if (active) FontWeight.Medium else FontWeight.Normal,
        color = if (active) BinanceColors.Yellow else BinanceColors.Body,
        modifier = Modifier
            .clip(RoundedCornerShape(8.dp))
            .background(if (active) BinanceColors.Card else BinanceColors.Dark)
            .clickable(onClick = onClick)
            .padding(horizontal = 16.dp, vertical = 8.dp),
    )
}

@Composable
fun TickerBar(quotes: List<IndexQuote> = MockStockData.indexQuotes) {
  var segmentWidth by remember { mutableFloatStateOf(0f) }
  val density = LocalDensity.current
  val scrollDuration = remember(segmentWidth, density) {
    if (segmentWidth <= 0f) 30000
    else ((segmentWidth / (50f * density.density)) * 1000f).roundToInt().coerceIn(20000, 60000)
  }

  val infiniteTransition = rememberInfiniteTransition(label = "ticker")
  val offset by infiniteTransition.animateFloat(
    initialValue = 0f,
    targetValue = if (segmentWidth > 0f) -segmentWidth else 0f,
    animationSpec = infiniteRepeatable(
      animation = tween(scrollDuration, easing = LinearEasing),
      repeatMode = RepeatMode.Restart,
    ),
    label = "tickerOffset",
  )

  Box(
    modifier = Modifier
      .fillMaxWidth()
      .height(40.dp)
      .clipToBounds()
      .background(BinanceColors.Card)
      .border(1.dp, BinanceColors.Hairline),
    contentAlignment = Alignment.CenterStart,
  ) {
    Row(
      modifier = Modifier
        .wrapContentWidth(unbounded = true)
        .offset { IntOffset(offset.roundToInt(), 0) },
      verticalAlignment = Alignment.CenterVertically,
    ) {
      repeat(2) { index ->
        TickerContentRow(
          quotes = quotes,
          modifier = if (index == 0) {
            Modifier.onGloballyPositioned { coordinates ->
              val width = coordinates.size.width.toFloat()
              if (width > 0f && width != segmentWidth) {
                segmentWidth = width
              }
            }
          } else {
            Modifier
          },
        )
      }
    }
  }
}

@Composable
private fun TickerContentRow(
  quotes: List<IndexQuote>,
  modifier: Modifier = Modifier,
) {
  Row(
    modifier = modifier
      .wrapContentWidth(unbounded = true)
      .padding(horizontal = 16.dp),
    horizontalArrangement = Arrangement.spacedBy(32.dp),
    verticalAlignment = Alignment.CenterVertically,
  ) {
    quotes.forEach { quote -> IndexQuoteChip(quote) }
  }
}

@Composable
private fun IndexQuoteChip(quote: IndexQuote) {
  val changeColor = if (quote.isUp) BinanceColors.Green else BinanceColors.Red
  Row(
    horizontalArrangement = Arrangement.spacedBy(8.dp),
    verticalAlignment = Alignment.CenterVertically,
    modifier = Modifier.wrapContentWidth(unbounded = true),
  ) {
    Text(quote.name, fontSize = 14.sp, color = BinanceColors.Body, maxLines = 1, softWrap = false)
    Text(
      quote.price,
      fontSize = 14.sp,
      fontFamily = LocalAppFontFamily.current,
      fontWeight = FontWeight.SemiBold,
      color = changeColor,
      maxLines = 1,
      softWrap = false,
    )
    Text(
      quote.changePercent,
      fontSize = 13.sp,
      fontFamily = LocalAppFontFamily.current,
      color = changeColor,
      maxLines = 1,
      softWrap = false,
    )
  }
}
